<template>
  <div
    class="form__input"
    :class="{'form__input--error': !!error }"
  >
    <input
      :id="id"
      :value="displayValue"
      :disabled="disabled"
      :class="{ 'input': true, 'input__error': !!error,
                'has-input': true, 'multi-select-input': true,
                'focus': hasFocus || showOptions}"
      :name="name"
      :required="required"
      readonly
      @focus="showOptionsDisplay()"
      @click="showOptionsDisplay()"
      @keydown.9="tabAway()"
    >
    <div
      v-if="showOptions"
      v-click-outside="hideOptions"
      class="drop-down-muti-options-container"
      @keydown.9="tabAway()"
    >
      <ul>
        <li>
          <input
            id="select-all"
            v-model="checkedOptions"
            type="checkbox"
            tabindex="-1"
            class="select-box"
            value="all"
            @change="selectAll($event)"
          >
          <label for="select-all">Select/Deselect All</label>
        </li>
        <li
          v-for="option in options"
          :key="option[keyField]"
        >
          <input
            :id="'muti-dropdown-' + option[keyField]"
            v-model="checkedOptions"
            :value="option[keyField]"
            type="checkbox"
            tabindex="-1"
            class="select-box"
            @change="updateSelect($event)"
          >
          <label :for="'muti-dropdown-' + option[keyField]">{{ option[displayField] }}</label>
        </li>
      </ul>
    </div>
    <label
      v-if="label"
      :for="id"
      class="form__label"
    >
      {{ label }}
    </label>

    <p
      v-show="error"
      class="form__error"
    >
      {{ error }}
    </p>

    <font-awesome-icon
      :icon="['fas', 'sort']"
      class="icon alt"
    />
  </div>
</template>

<script>
export default {
  $_veeValidate: {
    name() {
      return this.label;
    },
    value() {
      return this.value;
    },
  },
  props: {
    value: {
      type: String,
      required: true,
    },
    keyField: {
      type: String,
      default: 'id',
    },
    displayField: {
      type: String,
      default: 'display_label',
    },
    options: {
      type: Array, // [{id,display_label}]
      required: true,
    },
    label: {
      type: String,
      default: 'Form Label',
    },
    name: {
      type: String,
      default: 'form-name',
    },
    id: {
      type: String,
      default: 'form-id',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: '',
      required: false,
    },
    // eslint-disable-next-line vue/require-default-prop
    onChange: {
      type: Function,
    },
  },
  data() {
    return {
      hasFocus: false,
      showOptions: false,
      displayValue: '',
      checkedOptions: (this.value === 'all' || this.value.includes('all'))
        ? ['all', ...this.options.map(x => x[this.keyField])]
        : this.value.split(','),
    };
  },
  watch: {
    value(newValue) {
      if (newValue === 'all') {
        this.checkedOptions = ['all', ...this.options.map(x => x[this.keyField])];
      }
      this.updateDisplayValue();
    },
    options(newValue) {
      this.checkedOptions = this.value.includes('all')
        ? ['all', ...newValue.map(x => x[this.keyField])]
        : this.value.split(',');
    },
  },
  mounted() {
    this.updateDisplayValue();
  },
  methods: {
    updateValue() {
      this.$emit('input', this.buildReturnString(this.checkedOptions));
      this.updateDisplayValue();
    },
    updateDisplayValue() {
      if (this.checkedOptions && (this.checkedOptions.includes('all') || this.checkedOptions.length === 0)) {
        this.displayValue = `All ${this.label}s`;
      } else if (this.checkedOptions && this.checkedOptions.length === 1) {
        const foundOption = this.options.find(o => `${o[this.keyField]}` === `${this.checkedOptions[0]}`);
        if (!foundOption) {
          this.displayValue = `Some ${this.label}s`;
        } else {
          this.displayValue = foundOption[this.displayField];
        }
      } else {
        this.displayValue = `Some ${this.label}s`;
      }
    },
    changeValue() {
      this.$emit('change', 'input', this.buildReturnString(this.checkedOptions));

      if (this.onChange) {
        this.onChange();
      }
    },
    buildReturnString(options) {
      return options.includes('all')
        ? 'all'
        : this.checkedOptions.filter(x => x.length > 0).join(',');
    },
    showOptionsDisplay() {
      this.hasFocus = true;
      this.showOptions = true;
    },
    hideOptions() {
      if (!this.hasFocus) {
        this.showOptions = false;
      }
      this.hasFocus = false;
    },
    tabAway() {
      this.hasFocus = false;
      this.showOptions = false;
    },
    selectAll(event) {
      if (event.target.checked) {
        this.checkedOptions = ['all', ...this.options.map(x => x[this.keyField])];
      } else {
        this.checkedOptions = [];
      }
      this.changeValue();
      this.updateValue();
    },
    updateSelect() {
      // Handle add remove 'all' option based on change
      if (this.checkedOptions.length >= this.options.length) {
        if (!this.checkedOptions.includes('all')) {
          this.checkedOptions = [...this.checkedOptions, 'all'];
        }
      }
      if (this.checkedOptions.length <= this.options.length) {
        if (this.checkedOptions.includes('all')) {
          this.checkedOptions = this.checkedOptions.filter(x => x !== 'all');
        }
      }
      this.changeValue();
      this.updateValue();
    },
  },
};
</script>

<style lang="scss" scoped>
.icon {
  position: absolute;
  top: 22px;
  right: 15px;
  pointer-events: none;
}

.multi-select-input {
  cursor: pointer !important;
  user-select: none;
}

.drop-down-muti-options-container {
  position: absolute;
  top: 60px;
  z-index: 400;
  width: 100%;
  max-height: 20vh;
  overflow: auto;
  background-color: white;
  border: 1px solid rgba($black, 0.3);
  border-radius: 8px;
  box-shadow: 0 8px 15px -8px rgba(0, 0, 0, 0.82);

  & ul {
    margin: 0;
    padding: 0 !important;
    list-style-type: none;

    & li {
      display: flex;
      align-items: center;
      margin: 0;
      padding-left: 18px;

      &:hover {
        background: rgba(30, 144, 255, 1);

        & label {
          color: white;
        }
      }

      & input {
        margin: 0 !important;
        cursor: default !important;
      }

      & label {
        flex: 1;
        margin-bottom: 0;
        padding-left: 4px;
        color: $black;
      }
    }
  }

  & input.select-box {
    width: inherit !important;
    height: inherit !important;
  }
}
</style>
